import { HubStatusHistoryModel } from "@/types/models";
import { privateApi } from "./api";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const getHubStatusHistory = async (macAddress: string) => {
    return await privateApi.get<HubStatusHistoryModel[]>(`/hub/${macAddress}/status-history`);
};

export interface HubLogFile {
    name: string;
    size: number;
    lastModified: string | null;
}

const getHubLogs = async (macAddress: string) => {
    return await privateApi.get<HubLogFile[]>(`/hub/${macAddress}/logs`);
};

const getHubLog = async (macAddress: string, fileName: string) => {
    return await privateApi.get<string>(
        `/hub/${macAddress}/logs/${encodeURIComponent(fileName)}`,
        // Keep the payload as raw text — the log body is not JSON.
        { responseType: 'text', transformResponse: (data) => data },
    );
};

/** A picture stored under a hub's images/ prefix, with a URL to display it. */
const deleteHubStatusesBefore = async ({
    macAddress,
    before,
}: {
    macAddress: string;
    before: string;
}) => {
    return await privateApi.delete<{ deleted: number }>(
        `/hub/${macAddress}/status-history`,
        { params: { before } },
    );
};

/**
 * Backoffice-only: drop a hub's reported statuses from before a cutoff. The
 * Field page's dot grid is built from those rows, so it is refreshed too.
 */
export const useDeleteHubStatusesBefore = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteHubStatusesBefore,
        onSuccess: (_, { macAddress }) => {
            queryClient.invalidateQueries({
                queryKey: ['hub-status-history', macAddress],
            });
            queryClient.invalidateQueries({
                queryKey: ['monitoring', 'grid'],
            });
        },
    });
};

/** Hub settings the backoffice can change, on the hub itself (no project). */
export interface HubSettings {
    /** The acquisition schedule, as the cron the hub itself reads. */
    acquisitionScheduleCron?: string;
    codeUpdateIntervalDays?: number;
    /**
     * The SIM's mobile network operator, as a plain name — the page offers the
     * ones we buy from, but any name goes through. Unlike the two above, this
     * and the ICCID stay on the platform: the hub neither reads nor reports
     * them. Send an empty string to clear.
     */
    simOperator?: string;
    /** The ICCID printed on the SIM: 18–22 digits, or '' to clear. */
    simIccid?: string;
    /**
     * The modem model the SIM is in, e.g. EG25G. Platform-side like the SIM
     * fields, any name goes through, '' to clear.
     */
    modem?: string;
    /**
     * The branches the hub deploys itself from, written into its Hub.json like
     * the schedule above. A key the hub's config does not carry yet is written
     * for the first time, so either can be sent for any hub — but never empty:
     * a branch is set, never cleared.
     */
    scanorhizePiBranch?: string;
    humeosAnsibleBranch?: string;
}

const updateHubSettings = async ({
    macAddress,
    settings,
}: {
    macAddress: string;
    settings: HubSettings;
}) => {
    return await privateApi.patch(`/hub/${macAddress}`, settings);
};

/**
 * Backoffice-only: change a hub's settings. They live in the hub's Hub.json on
 * S3 — the backend writes the file and mirrors it — so this is a change to the
 * device, not just to a display.
 */
export const useUpdateHubSettings = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateHubSettings,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['monitoring', 'grid'] });
            queryClient.invalidateQueries({ queryKey: ['hubs'] });
            queryClient.invalidateQueries({ queryKey: ['field-overview'] });
        },
    });
};

export interface HubImage {
    name: string;
    size: number;
    lastModified: string | null;
    url: string;
}

const getHubImages = async (macAddress: string) => {
    return await privateApi.get<HubImage[]>(`/hub/${macAddress}/images`);
};

const addHubImage = async ({
    macAddress,
    file,
}: {
    macAddress: string;
    file: File;
}) => {
    const body = new FormData();
    body.append('file', file);
    return await privateApi.post<HubImage>(`/hub/${macAddress}/images`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

const deleteHubImage = async ({
    macAddress,
    fileName,
}: {
    macAddress: string;
    fileName: string;
}) => {
    return await privateApi.delete(
        `/hub/${macAddress}/images/${encodeURIComponent(fileName)}`,
    );
};

/** Something that happened to a hub, recorded from the backoffice. */
export interface HubIncident {
    id: number;
    name: string;
    /** ISO instant at UTC midnight of the day it happened. */
    occurredAt: string;
    description: string;
    createdAt: string;
    updatedAt: string;
}

export interface HubIncidentInput {
    name: string;
    /** `YYYY-MM-DD`, read as a UTC day. */
    occurredAt: string;
    description: string;
}

const getHubIncidents = async (macAddress: string) => {
    return await privateApi.get<HubIncident[]>(`/hub/${macAddress}/incidents`);
};

const createHubIncident = async ({
    macAddress,
    incident,
}: {
    macAddress: string;
    incident: HubIncidentInput;
}) => {
    return await privateApi.post<HubIncident>(
        `/hub/${macAddress}/incidents`,
        incident,
    );
};

const updateHubIncident = async ({
    macAddress,
    id,
    incident,
}: {
    macAddress: string;
    id: number;
    incident: Partial<HubIncidentInput>;
}) => {
    return await privateApi.patch<HubIncident>(
        `/hub/${macAddress}/incidents/${id}`,
        incident,
    );
};

const deleteHubIncident = async ({
    macAddress,
    id,
}: {
    macAddress: string;
    id: number;
}) => {
    return await privateApi.delete(`/hub/${macAddress}/incidents/${id}`);
};

/** A row of the Hub table, as the backoffice's Hubs page lists it. */
export interface BackOfficeHub {
    macAddress: string;
    version: string;
    acquisitionSchedule: string;
    availableMemoryGB: number | null;
    diskUsagePercent: number | null;
    codeUpdateIntervalDays: number | null;
    simOperator: string | null;
    simIccid: string | null;
    modem: string | null;
    scanorhizePiBranch: string | null;
    humeosAnsibleBranch: string | null;
    modemOperator: string | null;
    modemApn: string | null;
    createdAt: string;
    updatedAt: string;
    lastSeenAt: string | null;
    scanners: {
        serialNumber: string;
        projectId: string | null;
        projectName: string | null;
    }[];
}

export const useBackOfficeHubs = () =>
    useQuery({
        // Under 'hubs', so a settings change refreshes it.
        queryKey: ['hubs', 'backoffice'],
        queryFn: async () =>
            (await privateApi.get<BackOfficeHub[]>('/hub')).data,
    });

export const useHubStatusHistory = (macAddress?: string) => {
    return useQuery({
        queryKey: ['hub-status-history', macAddress],
        queryFn: () => {
            if (!macAddress) return Promise.reject("Mac Address missing");
            return getHubStatusHistory(macAddress).then((response) => response.data);
        },
        enabled: !!macAddress,
    });
};

export const useHubLogs = (macAddress?: string) => {
    return useQuery({
        queryKey: ['hub-logs', macAddress],
        queryFn: () => {
            if (!macAddress) return Promise.reject("Mac Address missing");
            return getHubLogs(macAddress).then((response) => response.data);
        },
        enabled: !!macAddress,
    });
};

export const useHubImages = (macAddress?: string) => {
    return useQuery({
        queryKey: ['hub-images', macAddress],
        queryFn: () => {
            if (!macAddress) return Promise.reject("Mac Address missing");
            return getHubImages(macAddress).then((response) => response.data);
        },
        enabled: !!macAddress,
    });
};

export const useAddHubImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: addHubImage,
        onSuccess: (_, { macAddress }) => {
            queryClient.invalidateQueries({
                queryKey: ['hub-images', macAddress],
            });
        },
    });
};

export const useDeleteHubImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteHubImage,
        onSuccess: (_, { macAddress }) => {
            queryClient.invalidateQueries({
                queryKey: ['hub-images', macAddress],
            });
        },
    });
};

export const useHubIncidents = (macAddress?: string) => {
    return useQuery({
        queryKey: ['hub-incidents', macAddress],
        queryFn: () => {
            if (!macAddress) return Promise.reject("Mac Address missing");
            return getHubIncidents(macAddress).then((response) => response.data);
        },
        enabled: !!macAddress,
    });
};

/** One mutation hook per verb, all refreshing the hub's incident list. */
function useIncidentMutation<V extends { macAddress: string }, R>(
    mutationFn: (variables: V) => Promise<R>,
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: (_, { macAddress }) => {
            queryClient.invalidateQueries({
                queryKey: ['hub-incidents', macAddress],
            });
        },
    });
}

export const useCreateHubIncident = () =>
    useIncidentMutation(createHubIncident);
export const useUpdateHubIncident = () =>
    useIncidentMutation(updateHubIncident);
export const useDeleteHubIncident = () =>
    useIncidentMutation(deleteHubIncident);

export const useHubLog = (macAddress?: string, fileName?: string) => {
    return useQuery({
        queryKey: ['hub-log', macAddress, fileName],
        queryFn: () => {
            if (!macAddress || !fileName)
                return Promise.reject("Mac Address or file name missing");
            return getHubLog(macAddress, fileName).then((response) => response.data);
        },
        enabled: !!macAddress && !!fileName,
    });
};
